import type { AxiosInstance } from "axios";
import { getTranslation } from "@mivo/i18n";
import { applyOptimisticMutation, mapTempId, persistGetResponse } from "@/domains/offline/cache/cacheWriter";
import { OFFLINE_TRANSLATIONS } from "@/domains/offline/constants";
import { mivoDb } from "@/domains/offline/db/mivoDb";
import {
  countPendingOutbox,
  incrementOutboxRetry,
  listFailedOutbox,
  listPendingOutbox,
  markOutboxFailed,
  removeOutbox,
} from "@/domains/offline/outbox/outboxStore";
import { emitOutboxQueued } from "@/domains/offline/sync/syncEvents";
import { setSyncState } from "@/domains/offline/sync/syncEvents";
import { getConnectivityOnline } from "@/domains/offline/connectivity/connectivityStore";
import { randomUUID } from "@/domains/shared/services/randomUUID";

const MAX_RETRIES = 3;

function replaceTempIdsInBody(body: unknown, mappings: Map<string, string>): unknown {
  if (body == null || mappings.size === 0) {
return body;
}
  const json = JSON.stringify(body);
  let next = json;
  mappings.forEach((serverId, tempId) => {
    next = next.split(tempId).join(serverId);
  });
  return JSON.parse(next) as unknown;
}

async function loadIdMappings(): Promise<Map<string, string>> {
  const rows = await mivoDb.idMappings.toArray();
  return new Map(rows.map((r) => [r.tempId, r.serverId]));
}

async function replayOutboxItem(api: AxiosInstance, item: Awaited<ReturnType<typeof listPendingOutbox>>[number]): Promise<void> {
  const mappings = await loadIdMappings();
  const body = replaceTempIdsInBody(item.body, mappings);
  const config = {
    method: item.method,
    url: item.url,
    data: body,
  };
  const res = await api.request(config);
  const data = res.data as Record<string, unknown> | undefined;
  const id = data?.id != null ? String(data.id) : undefined;
  const bodyRecord = body as Record<string, unknown> | undefined;
  const clientId = bodyRecord?.clientId != null ? String(bodyRecord.clientId) : undefined;

  if (id && clientId && item.method.toLowerCase() === "post") {
    const entity = item.url.includes("/goals") ? "goal" : item.url.includes("/evolutions") ? "evolution" : "entity";
    await mapTempId(clientId, id, entity);
  }

  if (item.method.toLowerCase() === "get") {
    await persistGetResponse(item.url, res.data, undefined);
  } else if (res.data != null) {
    await persistGetResponse(item.url, res.data, undefined);
  }

  await removeOutbox(item.id);
}

export async function flushOutbox(api: AxiosInstance): Promise<void> {
  if (!getConnectivityOnline()) {
return;
}

  const pending = await listPendingOutbox();
  if (pending.length === 0) {
    const failed = await listFailedOutbox();
    setSyncState("idle", { pendingCount: 0, failedCount: failed.length });
    return;
  }

  setSyncState("syncing", { pendingCount: pending.length });

  for (const item of pending) {
    try {
      await replayOutboxItem(api, item);
    } catch (err: unknown) {
      const ax = err as { response?: { status?: number } };
      const status = ax.response?.status;
      if (status != null && status >= 400 && status < 500 && status !== 408 && status !== 429) {
        await markOutboxFailed(item.id, `HTTP ${status}`);
        continue;
      }
      if (item.retries + 1 >= MAX_RETRIES) {
        await markOutboxFailed(item.id, getTranslation(OFFLINE_TRANSLATIONS.networkFailure));
      } else {
        await incrementOutboxRetry(item.id);
      }
      setSyncState("error", { pendingCount: await countPendingOutbox(), failedCount: (await listFailedOutbox()).length });
      return;
    }
  }

  const failed = await listFailedOutbox();
  setSyncState("idle", { pendingCount: 0, failedCount: failed.length });
}

export async function pullParentScope(api: AxiosInstance): Promise<void> {
  if (!getConnectivityOnline()) {
return;
}

  setSyncState("syncing");

  try {
    const [patientsRes, sessionsRes] = await Promise.all([api.get("/patients"), api.get("/sessions")]);

    await persistGetResponse("/patients", patientsRes.data);
    await persistGetResponse("/sessions", sessionsRes.data);

    await mivoDb.syncMeta.put({
      key: "parentScopePulledAt",
      value: new Date().toISOString(),
      updatedAt: Date.now(),
    });
    setSyncState("idle", { pendingCount: await countPendingOutbox() });
  } catch {
    setSyncState("error");
    throw new Error("prefetch failed");
  }
}

export async function pullTherapistScope(api: AxiosInstance): Promise<void> {
  if (!getConnectivityOnline()) {
return;
}

  setSyncState("syncing");

  try {
    const [patientsRes, sessionsRes] = await Promise.all([api.get("/patients"), api.get("/sessions")]);
    await persistGetResponse("/patients", patientsRes.data);
    await persistGetResponse("/sessions", sessionsRes.data);

    await mivoDb.syncMeta.put({ key: "therapistScopePulledAt", value: new Date().toISOString(), updatedAt: Date.now() });
    setSyncState("idle", { pendingCount: await countPendingOutbox() });
  } catch {
    setSyncState("error");
    throw new Error("prefetch failed");
  }
}

export async function pullOfflineScope(api: AxiosInstance, role: string): Promise<void> {
  if (role === "parent") {
    await pullParentScope(api);
    return;
  }

  if (role === "therapist" || role === "admin") {
    await pullTherapistScope(api);
  }
}

export async function queueOfflineMutation(
  api: AxiosInstance,
  method: string,
  url: string,
  body: unknown,
): Promise<string> {
  const id = randomUUID();
  await mivoDb.outbox.put({
    id,
    method: method.toUpperCase(),
    url,
    body,
    createdAt: Date.now(),
    retries: 0,
    status: "pending",
  });
  await applyOptimisticMutation(method, url, body);
  emitOutboxQueued(id);
  void flushOutbox(api);
  return id;
}
